/**
 * Shrink an image in the browser, before a single byte is uploaded.
 *
 * This is deliberately client-side rather than a worker job, and the reason is
 * the dominant case: someone presses ⌘⇧4 on a Retina screen and pastes the
 * result. That is a 4–12 MB PNG of a 5120×2880 screenshot, which re-encodes to
 * a few hundred KB of WebP with nothing visibly lost. Doing it here means the
 * upload is twenty times smaller, it finishes while they are still typing, and
 * the expensive bytes are never stored — the cheapest byte is the one that was
 * never sent.
 *
 * A server-side job would cost a download, a re-encode, an upload and a second
 * copy in the bucket, all to reach the same place several seconds later. The one
 * thing it would buy — being authoritative — does not matter here, because the
 * store enforces the real size cap itself and the server records the true size
 * it finds. If a browser skips this step, the upload is merely larger.
 *
 * **Nothing here may fail an upload.** Every branch falls back to the original
 * file. An image that cannot be decoded, a browser without OffscreenCanvas, a
 * codec that refuses — all of them mean "send it as it is", never "you cannot
 * attach this".
 */

/** Long edge, in CSS pixels. A Retina screenshot stays readable at this width. */
const MAX_EDGE = 2560;

/** WebP at this quality is visually indistinguishable for screenshots and UI. */
const QUALITY = 0.85;

/**
 * Below this, re-encoding is not worth the risk of making a well-optimised file
 * bigger. Most pasted screenshots are far above it.
 */
const SKIP_BELOW_BYTES = 256 * 1024;

/**
 * Types that must be passed through untouched.
 *
 *   GIF   re-encoding to WebP through a canvas keeps only the first frame, so
 *         an animation silently becomes a still — worse than not optimising.
 *   SVG   vector already, and the API refuses it anyway.
 *   HEIC  Safari can decode it but most browsers cannot, and a failed decode
 *         here would be a pointless round trip.
 */
const PASS_THROUGH: ReadonlySet<string> = new Set(['image/gif', 'image/svg+xml', 'image/heic', 'image/heif']);

export interface OptimiseResult {
  readonly file: File;
  /** True when the returned file is a re-encode rather than the original. */
  readonly optimised: boolean;
  readonly originalBytes: number;
}

export async function optimiseImage(file: File): Promise<OptimiseResult> {
  const unchanged: OptimiseResult = { file, optimised: false, originalBytes: file.size };

  if (!file.type.startsWith('image/')) return unchanged;
  if (PASS_THROUGH.has(file.type)) return unchanged;
  if (file.size < SKIP_BELOW_BYTES) return unchanged;

  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const blob = await encode(bitmap, width, height);
    bitmap.close();

    // Re-encoding can make an already-efficient JPEG larger. If it did, the
    // original wins — the point is fewer bytes, not WebP for its own sake.
    if (blob === null || blob.size >= file.size) return unchanged;

    return {
      file: new File([blob], withExtension(file.name, 'webp'), {
        type: 'image/webp',
        lastModified: file.lastModified,
      }),
      optimised: true,
      originalBytes: file.size,
    };
  } catch {
    // Decode failure, missing API, blocked codec — all mean "send the original".
    return unchanged;
  }
}

async function encode(bitmap: ImageBitmap, width: number, height: number): Promise<Blob | null> {
  // OffscreenCanvas keeps the work off the main thread's paint path, which
  // matters when someone pastes several screenshots at once.
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(width, height);
    const context = canvas.getContext('2d');
    if (context === null) return null;
    context.drawImage(bitmap, 0, 0, width, height);
    return canvas.convertToBlob({ type: 'image/webp', quality: QUALITY });
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (context === null) return null;
  context.drawImage(bitmap, 0, 0, width, height);

  return new Promise<Blob | null>((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/webp', QUALITY);
  });
}

/** `screenshot.png` → `screenshot.webp`, keeping a name with no extension intact. */
function withExtension(filename: string, extension: string): string {
  const base = filename.replace(/\.[A-Za-z0-9]{1,8}$/, '');
  return `${base || 'image'}.${extension}`;
}

/** Human-readable size, for the upload chip. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
